import { useMemo, useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { nomCategorie } from '../../analysis/donnees'
import { bilanAnnuel, type DetteTiers } from '../../analysis/statistiques'
import { formatMontant } from '../../domain/argent'
import { anneeDe, aujourdhui, formatJour, formatJourCourt } from '../../domain/temps'
import { Carte, Encart, Entete } from '../composants'
import { useDonnees } from '../donnees'

type Cle = 'avance' | 'organismes' | 'personnes' | 'theorique' | 'ouvertes' | 'effectif'

export function PageBilan() {
  const { categorieId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const { ix } = useDonnees()
  const jour = aujourdhui()
  const annee = Number(params.get('annee') ?? anneeDe(jour))
  // Pour une annee close, on observe les versements a ce jour : un
  // remboursement recu en janvier pour une facture de decembre compte bien.
  const auJour = jour
  const [ouvert, setOuvert] = useState<Cle | null>(null)

  const b = useMemo(() => bilanAnnuel(ix, categorieId, annee, auJour), [ix, categorieId, annee, auJour])
  const basculer = (c: Cle) => setOuvert((o) => (o === c ? null : c))

  const tiersPersonnes = b.personnes.parTiers.filter((t) => t.restant > 0)

  return (
    <>
      <Entete titre={nomCategorie(ix, categorieId)} sous={`Bilan ${annee}`} retour />
      <main className="contenu">
        <div className="nav-mois">
          <button type="button" className="btn btn--petit" onClick={() => setParams({ annee: String(annee - 1) }, { replace: true })}>{annee - 1}</button>
          <span className="nav-mois__titre">{annee}</span>
          <button type="button" className="btn btn--petit" onClick={() => setParams({ annee: String(annee + 1) }, { replace: true })}>{annee + 1}</button>
        </div>

        {b.nbOperations === 0 ? (
          <p className="vide">Aucune dépense dans cette catégorie en {annee}.</p>
        ) : (
          <Carte titre={`${b.nbOperations} dépense${b.nbOperations > 1 ? 's' : ''}`}>
            <div className="bilan">
              <Ligne op="" libelle="Total avancé" valeur={b.totalAvance} ouvert={ouvert === 'avance'} onClick={() => basculer('avance')}>
                <ListeOps lignes={b.lignes.map((l) => ({ id: l.operation.id, date: l.operation.date, libelle: l.operation.libelle, montant: l.total }))} />
              </Ligne>

              <Ligne op="−" libelle="Part des assurances" valeur={b.organismes.du} ouvert={ouvert === 'organismes'} onClick={() => basculer('organismes')}
                note={b.organismes.du > 0 ? `encaissé ${formatMontant(b.organismes.encaisse)}` : undefined}>
                <Dettes dettes={b.organismes.parTiers} />
              </Ligne>

              <Ligne op="−" libelle="Part refacturée" valeur={b.personnes.du} ouvert={ouvert === 'personnes'} onClick={() => basculer('personnes')}
                note={b.personnes.du > 0 ? `encaissé ${formatMontant(b.personnes.encaisse)}` : undefined}>
                <Dettes dettes={b.personnes.parTiers} />
              </Ligne>

              <Ligne op="=" libelle="Reste à ma charge (théorique)" valeur={b.resteTheorique} total ouvert={ouvert === 'theorique'} onClick={() => basculer('theorique')}>
                <p className="tres-doux">Ce que vous devriez supporter si chacun avait payé sa part.</p>
                <ListeOps lignes={b.lignes.filter((l) => l.partMoi > 0).map((l) => ({ id: l.operation.id, date: l.operation.date, libelle: l.operation.libelle, montant: l.partMoi, detail: l.partMoi === l.total ? 'non partagée' : `sur ${formatMontant(l.total)}` }))} />
              </Ligne>

              <Ligne op="+" libelle="Encore non encaissé" valeur={b.creancesOuvertes} ouvert={ouvert === 'ouvertes'} onClick={() => basculer('ouvertes')}>
                <ListeOps lignes={b.lignes.filter((l) => l.ouvert > 0).map((l) => ({ id: l.operation.id, date: l.operation.date, libelle: l.operation.libelle, montant: l.ouvert }))} />
              </Ligne>

              <Ligne op="=" libelle={`Reste à ma charge (effectif, au ${formatJour(auJour)})`} valeur={b.resteEffectif} vedette ouvert={ouvert === 'effectif'} onClick={() => basculer('effectif')}>
                <p className="tres-doux">Ce qui est réellement sorti de votre poche à ce jour.</p>
              </Ligne>
            </div>
          </Carte>
        )}

        {tiersPersonnes.map((t) => (
          <Encart key={t.tiers.id} ton="alerte">
            {t.tiers.nom} doit encore <strong>{formatMontant(t.restant)}</strong> sur cette catégorie en {annee}.{' '}
            <Link to={`/justificatif?tiers=${t.tiers.id}&du=${annee}-01-01&au=${annee}-12-31`}>Éditer le justificatif</Link>
          </Encart>
        ))}
      </main>
    </>
  )
}

function Ligne({
  op,
  libelle,
  valeur,
  note,
  total,
  vedette,
  ouvert,
  onClick,
  children,
}: {
  op: string
  libelle: string
  valeur: number
  note?: string
  total?: boolean
  vedette?: boolean
  ouvert: boolean
  onClick: () => void
  children: ReactNode
}) {
  const classe = ['bilan__ligne', total && 'bilan__ligne--total', vedette && 'bilan__ligne--vedette'].filter(Boolean).join(' ')
  return (
    <>
      <button type="button" className={classe} aria-expanded={ouvert} onClick={onClick}>
        <span className="bilan__op">{op}</span>
        <span>
          {libelle}
          {note && <span className="tres-doux"> · {note}</span>}
        </span>
        <span className="montant">{formatMontant(valeur)}</span>
      </button>
      {ouvert && <div className="bilan__detail">{children}</div>}
    </>
  )
}

function ListeOps({ lignes }: { lignes: { id: string; date: string; libelle: string; montant: number; detail?: string }[] }) {
  if (!lignes.length) return <p className="tres-doux">Aucune.</p>
  return (
    <ul className="pile pile--serree" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {lignes.map((l) => (
        <li key={l.id} className="ligne ligne--espacee">
          <Link to={`/operations/${l.id}`}>
            {formatJourCourt(l.date)} {l.libelle}
            {l.detail && <span className="tres-doux"> ({l.detail})</span>}
          </Link>
          <span className="mono">{formatMontant(l.montant)}</span>
        </li>
      ))}
    </ul>
  )
}

function Dettes({ dettes }: { dettes: DetteTiers[] }) {
  if (!dettes.length) return <p className="tres-doux">Aucune.</p>
  return (
    <table>
      <thead>
        <tr><th /><th className="nombre">Dû</th><th className="nombre">Encaissé</th><th className="nombre">Reste</th></tr>
      </thead>
      <tbody>
        {dettes.map((t) => (
          <tr key={t.tiers.id}>
            <td>{t.tiers.nom}</td>
            <td className="nombre">{formatMontant(t.du)}</td>
            <td className="nombre">{formatMontant(t.encaisse)}</td>
            <td className="nombre">{formatMontant(t.restant)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
